import React from 'react'
import moment from 'moment';
import { useParams, useHistory, useLocation } from 'react-router-dom'
import MainScreen from 'components/MainScreen';
import { TableList } from 'components/Common/TableList'
import { makeStyles } from '@material-ui/core/styles';
import { Fab, Snackbar, IconButton, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import { fetchEntities } from 'services/entities';
import { getSchemaFor } from "services/schemas";
import { ViewEntity } from './ViewEntity'
import { toStartCase } from 'util/string';

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2),
    },
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  title: {
    margin: theme.spacing(0, 2, 2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, 2, 1),
    }
  }
}));

function isRelationProperty(prop) {
  return prop && prop.relation && prop.relation.schemaId;
}

function getRelationTitle(entity, prop, relations) {
  if(prop.type === 'comma-separated-list' && entity[prop.name]) {
    const relationIds = entity[prop.name].split(',');
    return relationIds.map(relationId => getRelationIdTitle(entity, relationId, relations)).join(',');
  }
  return getRelationIdTitle(entity, entity[prop.name], relations);
}

function getRelationIdTitle(entity, relationId, relations) {
    const relation = relations && relations.find(relation => relation.id === relationId);
  const title = (relation && relation.title) || relationId;
  return title;
}

function titleOfSchema(schema) {
  return schema && toStartCase(schema.title || schema.name);
}

function titleOf(entity, schema) {
  const titles = (schema.entityTitleProps || ['name'])
    .map(prop => entity[prop])
    .filter(title => !!title);
  return toStartCase([titleOfSchema(schema), ...titles].join(' - '));
}

function valueOf(entity, prop, relations) {
  if(isRelationProperty(prop)) {
    return getRelationTitle(entity, prop, relations) || entity[prop.name];
  }
  if(prop.type === 'timestamp') {
    return moment.unix(entity[prop.name]).toString();
  }
  return entity[prop.name];
}

export default function Entity(props) {
  const classes = useStyles();

  const { schemaId, id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = React.useState(false);
  const [schema, setSchema] = React.useState(null);
  const [schemaPermissions, setSchemaPermissions] = React.useState({});
  const [columns, setColumns] = React.useState(null);
  const [entities, setEntities] = React.useState(null);
  const [relations, setRelations] = React.useState(null);
  const [selectedEntity, setSelectedEntity] = React.useState(null);
  const [message, setMessage] = React.useState(null);

  // TODO: too complex, refactor and optimise
  React.useEffect(() => {
    const restoreTitle = document.title;
    return () => { document.title = restoreTitle; };
  }, [])

  React.useEffect(() => {
    setEntities(null);
    setRelations(null);
    setSchema(null);
    setSchemaPermissions({});
    setColumns(null);
  }, [schemaId])

  React.useEffect(() => {
    id || setSelectedEntity(null);
  }, [id])

  React.useEffect(() => {
    if (!schema) {
      getSchemaFor(schemaId)
        .then(({schema, permissions} = {}) => {
          setSchema(schema);
          if(schema) {
            document.title = titleOfSchema(schema);
          }
          permissions && setSchemaPermissions(permissions);
        });
    }
  }, [schemaId, schema]);

  React.useEffect(() => {
    if (!entities && !loading) {
      setLoading(true);
      fetchEntities(schemaId, location.search)
        .then(( {results: entities, relations } = {} ) => {
          setEntities(entities || []);
          setRelations(relations || []);
          setLoading(false);
        })
        ;
    }
  }, [schemaId, loading, entities, location.search])

  React.useEffect(() => {
    if (schema && relations) {
      setColumns(
        (schema.properties || [])
          .filter(prop => !prop.hidden)
          .map(prop => ({
            name: prop.name,
            title: prop.title,
            value: entity => valueOf(entity, prop, relations)
          }))
      );
    }
  }, [schema, relations])

  React.useEffect(() => {
    if (!selectedEntity && entities && id && schema) {
      const entity = (id === 'new')
        ? {}
        : entities.find(entity => entity.id === id)
        ;
      setSelectedEntity(entity);
    }
    if (selectedEntity) {
      document.title = titleOf(selectedEntity, schema);
    }
  }, [id, entities, selectedEntity, schema]);

  const onFormClose = ({ refresh, message } = {}) => {
    history.length > 1 ? history.goBack() : history.replace(`/table/${schemaId}`); // TODO: better fix
    message && setMessage(message);
    if(refresh) {
      setEntities(null);
      setRelations(null);
    }
  }

  const closeSnackbar = () => setMessage(null);

  return <MainScreen {...props}>
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>{titleOfSchema(schema)}</Typography>
      <TableList
        entities={entities}
        columns={columns}
        exportFileName={`${titleOfSchema(schema)}.csv`}
        onClick={({ entity, column }) => history.push(`/table/${schemaId}/${entity.id}`)}
      />
    </div>
    {schemaPermissions.create && <Fab color="primary" className={classes.fab} aria-label="add"
      onClick={() => history.push(`/table/${schemaId}/new`)}
    >
      <AddIcon />
    </Fab>}
    {selectedEntity &&
      <ViewEntity
        schema={schema}
        title={`${id === 'new' ? 'Add' : schemaPermissions.update ? 'Edit' : 'View'}: ${titleOf(selectedEntity, schema)}`}
        entity={selectedEntity}
        onClose={onFormClose} 
        canUpdate={schemaPermissions.update} 
        canDelete={schemaPermissions.delete}
      />
    }
    <Snackbar
      open={!!message} autoHideDuration={6000} message={message} onClose={closeSnackbar}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={closeSnackbar}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </MainScreen>
    ;
}